/**
 * Les gestes de « Travail entre les lives », ceux de l'ancien écran (`StudyMissionPrescriptionScreen`) :
 *  - « Donner à faire » envoie « À ajouter » et rien d'autre (`prescribe`) ; « Enregistrer », sans
 *    rien à donner, replie la carte sans appel (le travail a été donné pendant le live) ;
 *  - « Annuler » sur une carte repliée : `unprescribe` pour un envoi, rien pour un « Enregistrer » ;
 *  - « Recommencer » ouvre le formulaire de cadrage (`useTutorPrescriptionRegenerate`) ; plus d'une
 *    proposition à remplacer, la page demande d'abord ; puis `regenerate` ;
 *  - « Ajouter un exercice » (`generateAdditional`), sur le chapitre du brouillon ;
 *  - la ⨯ d'une proposition la retire du brouillon (`removeDraftActivity`), sans rien demander ; celle
 *    d'une activité déjà à faire demande, puis `deleteActivitiesTodo` — le serveur peut répondre 200
 *    sans rien retirer (l'élève l'a commencée) : la page le dit ;
 *  - « Réessayer » relance la génération du champion pour cet élève ;
 *  - « Passer cette étape » / « Continuer vers le bilan » remplacent l'écran par `end-session-tutor`,
 *    avec les envois de la séance — le pont du bilan choisit son écran.
 * Un seul geste de chaque sorte à la fois, comme le legacy. Les refus du serveur passent par les
 * boîtes de la page ; ceux que le legacy disait par sa boîte d'erreur générique, par le bandeau.
 * Toutes les écritures sont mesurées (`prescription.*`, surface `tutor_prescription`).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useReducer, useRef } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { removeDraftActivity } from "../../../../../../data/redux/actions/studyMissions"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { studyMissionsService } from "../../../../../../data/services/studyMissionsService"
import {
    generateAdditionalBody,
    missionAlreadyOpened,
    prescribeBody,
    regenerateScope,
    removalApplied,
    removeActivityDialog,
} from "../../../../adapters/tutor/Lives/tutorPrescriptionAdapter"
import {
    tutorPrescriptionStore,
    tutorPrescriptionStudentsById,
    type TutorPrescriptionUi,
} from "../../../../view-models/tutor/Lives/tutorPrescriptionViewModel"
import { type UIActivityDisplayMode } from "../../../types"
import {
    type UITutorPrescriptionActivity,
    type UITutorPrescriptionDialog,
    type UITutorPrescriptionStudent,
} from "../../../types/tutorPrescription"
import { useGesture } from "../../../hooks/gestureTracking"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { generateParams } from "./useTutorPrescriptionData"
import { useTutorPrescriptionRegenerate } from "./useTutorPrescriptionRegenerate"

type Id = string | number

const SURFACE = "tutor_prescription"

/** Ce que dit le bandeau quand « Recommencer » ou « Ajouter un exercice » ne passe pas. */
export const REGENERATE_FAILED = "Je n'ai pas pu recommencer la séquence. Réessaie dans un instant."
const ADD_ACTIVITY_FAILED = "Je n'ai pas pu ajouter d'exercice. Réessaie dans un instant."

type Removal = { studentId: Id; activityId: Id; studentName: string; title: string }

/** Une boîte ouverte, et ce que son « oui » relance. */
type OpenDialog = {
    shown: UITutorPrescriptionDialog
    removal?: Removal
    regenerate?: { studentId: Id; options: Record<string, number | string> }
}

/** L'aperçu ouvert : une activité déjà à faire, ou une proposition du champion (par sa place). */
export type PrescriptionPreview =
    { kind: "existing"; studentId: Id; activityId: Id } | { kind: "proposal"; studentId: Id; index: number }

type ScreenState = {
    ui: TutorPrescriptionUi & { removal: Removal | null }
    dialog: OpenDialog | null
    preview: PrescriptionPreview | null
    /** La vue choisie dans l'aperçu ; `null` : la première offerte. */
    previewMode: UIActivityDisplayMode | null
    failure: string | null
}

const INITIAL: ScreenState = {
    ui: {
        regeneratingId: null,
        addingId: null,
        prescribingId: null,
        unprescribingId: null,
        removal: null,
        savedAsIs: {},
    },
    dialog: null,
    preview: null,
    previewMode: null,
    failure: null,
}

type Busy = "regeneratingId" | "addingId" | "prescribingId" | "unprescribingId"

type ScreenAction =
    | { type: "BUSY"; key: Busy; studentId: Id | null }
    | { type: "REMOVAL"; removal: Removal | null }
    | { type: "SAVED_AS_IS"; studentId: Id; saved: boolean }
    | { type: "DIALOG"; dialog: OpenDialog | null }
    | { type: "PREVIEW"; preview: PrescriptionPreview | null }
    | { type: "PREVIEW_MODE"; mode: UIActivityDisplayMode }
    | { type: "FAILURE"; message: string | null }

function prescriptionScreenReducer(state: ScreenState, action: ScreenAction): ScreenState {
    switch (action.type) {
        case "BUSY":
            return { ...state, ui: { ...state.ui, [action.key]: action.studentId } }
        case "REMOVAL":
            return { ...state, ui: { ...state.ui, removal: action.removal } }
        case "SAVED_AS_IS": {
            const savedAsIs = { ...state.ui.savedAsIs }
            if (action.saved) savedAsIs[String(action.studentId)] = true
            else delete savedAsIs[String(action.studentId)]
            return { ...state, ui: { ...state.ui, savedAsIs } }
        }
        case "DIALOG":
            return { ...state, dialog: action.dialog }
        case "PREVIEW":
            return { ...state, preview: action.preview, previewMode: null }
        case "PREVIEW_MODE":
            return { ...state, previewMode: action.mode }
        case "FAILURE":
            return { ...state, failure: action.message }
        default:
            return state
    }
}

type StackNavigation = { replace: (route: string, params?: object) => void }

export function useTutorPrescriptionState(args: { occurrenceId: Id | null; liveSessionId: Id | null }) {
    const { occurrenceId, liveSessionId } = args
    const navigation = useNavigation() as unknown as StackNavigation
    const dispatch = useDispatch()
    const reduxStore = useStore()
    const [state, dispatchUi] = useReducer(prescriptionScreenReducer, INITIAL)
    const { ui } = state

    const missions = useSelector((s: unknown) => tutorPrescriptionStore(s, occurrenceId))
    const busy = (key: Busy, studentId: Id | null) => dispatchUi({ type: "BUSY", key, studentId })

    // ── Les écritures ──
    // Le retrait en vol, relu à la réponse : le serveur répond 200 même sans rien retirer.
    const removalRef = useRef(ui.removal)
    removalRef.current = ui.removal
    const prescribe = useApiMutation({
        service: studyMissionsService.prescribe,
        gesture: "prescription.give",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: () => busy("prescribingId", null),
        onError: (_message, error) => {
            busy("prescribingId", null)
            dispatchUi({
                type: "DIALOG",
                dialog: { shown: { kind: "prescribe_failed", alreadyOpened: missionAlreadyOpened(error) } },
            })
        },
    })
    const unprescribe = useApiMutation({
        service: studyMissionsService.unprescribe,
        gesture: "prescription.undo",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: () => busy("unprescribingId", null),
        onError: (_message, error) => {
            busy("unprescribingId", null)
            dispatchUi({
                type: "DIALOG",
                dialog: { shown: { kind: "unprescribe_failed", alreadyOpened: missionAlreadyOpened(error) } },
            })
        },
    })
    const regenerate = useApiMutation({
        service: studyMissionsService.regenerate,
        gesture: "prescription.regenerate",
        surface: SURFACE,
        fallback: REGENERATE_FAILED,
        skipWhilePending: true,
        onSuccess: () => busy("regeneratingId", null),
        onError: (message) => {
            busy("regeneratingId", null)
            dispatchUi({ type: "FAILURE", message })
        },
    })
    const addActivity = useApiMutation({
        service: studyMissionsService.generateAdditional,
        gesture: "prescription.add_activity",
        surface: SURFACE,
        fallback: ADD_ACTIVITY_FAILED,
        skipWhilePending: true,
        onSuccess: () => busy("addingId", null),
        onError: (message) => {
            busy("addingId", null)
            dispatchUi({ type: "FAILURE", message })
        },
    })
    const removeTodo = useApiMutation({
        service: activitiesService.deleteActivitiesTodo,
        gesture: "prescription.remove_activity",
        surface: SURFACE,
        scopeParam: "student",
        skipWhilePending: true,
        onSuccess: (data) => {
            const removal = removalRef.current
            if (removal != null && !removalApplied(data, removal.activityId)) {
                dispatchUi({
                    type: "DIALOG",
                    dialog: {
                        shown: {
                            kind: "remove_not_applied",
                            studentName: removal.studentName,
                            activityTitle: removal.title,
                        },
                    },
                })
            }
            dispatchUi({ type: "REMOVAL", removal: null })
        },
        onError: () => {
            dispatchUi({ type: "REMOVAL", removal: null })
            dispatchUi({ type: "DIALOG", dialog: { shown: { kind: "remove_failed" } } })
        },
    })
    const retryDraft = useGesture("prescription.retry_draft", SURFACE)

    // ── « Recommencer » ──
    const sendRegenerate = useCallback(
        (studentId: Id, options: Record<string, number | string>) => {
            if (occurrenceId == null) return
            const sent = regenerate.start({}, { live_occurrence_id: occurrenceId, student_id: studentId, ...options })
            if (sent) busy("regeneratingId", studentId)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [occurrenceId, regenerate.start]
    )
    // Générer ne complète pas la liste, il la remplace : au-delà d'une proposition, on l'annonce.
    const onRegenerateSubmit = useCallback(
        (studentId: Id, options: Record<string, number | string>) => {
            const count = missions.drafts[String(studentId)]?.activities?.length ?? 0
            if (count > 1) {
                dispatchUi({
                    type: "DIALOG",
                    dialog: { shown: { kind: "replace_proposals", count }, regenerate: { studentId, options } },
                })
                return
            }
            sendRegenerate(studentId, options)
        },
        [missions.drafts, sendRegenerate]
    )
    const regenerateForm = useTutorPrescriptionRegenerate({ onSubmit: onRegenerateSubmit })

    const onRegenerate = useCallback(
        (student: UITutorPrescriptionStudent) => {
            if (ui.regeneratingId != null) return
            const key = String(student.id)
            const tutorStudent = tutorPrescriptionStudentsById(reduxStore.getState())[key]
            regenerate.gesture.opened()
            regenerateForm.openFor(student.id, regenerateScope(missions.drafts[key], tutorStudent))
        },
        [ui.regeneratingId, reduxStore, regenerate.gesture, regenerateForm, missions.drafts]
    )

    // ── La carte ──
    const onConfirm = useCallback(
        (student: UITutorPrescriptionStudent) => {
            const draft = missions.drafts[String(student.id)]
            if ((draft?.activities?.length ?? 0) === 0) {
                dispatchUi({ type: "SAVED_AS_IS", studentId: student.id, saved: true })
                return
            }
            if (ui.prescribingId != null || draft == null) return
            if (prescribe.start({}, prescribeBody(draft))) busy("prescribingId", student.id)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [missions.drafts, ui.prescribingId, prescribe.start]
    )

    const onUndo = useCallback(
        (student: UITutorPrescriptionStudent) => {
            if (missions.prescriptions[String(student.id)] == null) {
                dispatchUi({ type: "SAVED_AS_IS", studentId: student.id, saved: false })
                return
            }
            if (occurrenceId == null || ui.unprescribingId != null) return
            const sent = unprescribe.start({}, { live_occurrence_id: occurrenceId, student_id: student.id })
            if (sent) busy("unprescribingId", student.id)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [missions.prescriptions, occurrenceId, ui.unprescribingId, unprescribe.start]
    )

    const onAddActivity = useCallback(
        (student: UITutorPrescriptionStudent) => {
            if (occurrenceId == null || ui.addingId != null) return
            const body = generateAdditionalBody(occurrenceId, student.id, missions.drafts[String(student.id)])
            if (addActivity.start({}, body)) busy("addingId", student.id)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [occurrenceId, ui.addingId, missions.drafts, addActivity.start]
    )

    const onDropProposal = useCallback(
        (student: UITutorPrescriptionStudent, index: number) => {
            if (occurrenceId == null) return
            dispatch(removeDraftActivity({ liveOccurrenceId: occurrenceId, studentId: student.id, index }) as never)
        },
        [dispatch, occurrenceId]
    )

    const onAskRemoveActivity = useCallback(
        (student: UITutorPrescriptionStudent, activity: UITutorPrescriptionActivity) => {
            if (ui.removal != null) return
            const removal: Removal = {
                studentId: student.id,
                activityId: activity.id,
                studentName: student.name,
                title: activity.title,
            }
            removeTodo.gesture.opened()
            dispatchUi({
                type: "DIALOG",
                dialog: { shown: removeActivityDialog(removal.studentName, removal.title), removal },
            })
        },
        [ui.removal, removeTodo.gesture]
    )

    const onRetryDraft = useCallback(
        (student: UITutorPrescriptionStudent) => {
            if (occurrenceId == null) return
            const params = generateParams(occurrenceId, student.id)
            retryDraft.submitted()
            dispatch(
                apiRequest(studyMissionsService.generate, {}, params, params, null, null, {
                    offlineDialog: true,
                    onResult: (result: { ok: boolean; error?: unknown }) =>
                        result.ok ? retryDraft.succeeded() : retryDraft.failed(result.error),
                }) as never
            )
        },
        [dispatch, occurrenceId, retryDraft]
    )

    // ── Les boîtes ──
    const onDialogConfirm = useCallback(() => {
        const open = state.dialog
        dispatchUi({ type: "DIALOG", dialog: null })
        if (open?.regenerate != null) sendRegenerate(open.regenerate.studentId, open.regenerate.options)
        if (open?.removal != null) {
            const { removal } = open
            const sent = removeTodo.start({ student: removal.studentId }, { activity_ids: [removal.activityId] })
            if (sent) dispatchUi({ type: "REMOVAL", removal })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.dialog, sendRegenerate, removeTodo.start])

    // ── La sortie : le bilan, avec les envois de la séance ──
    const onContinue = useCallback(() => {
        navigation.replace("end-session-tutor", {
            occurrence_id: occurrenceId,
            liveSessionId,
            studyMissionPrescriptions: missions.prescriptions,
        })
    }, [navigation, occurrenceId, liveSessionId, missions.prescriptions])

    return {
        ui,
        preview: state.preview,
        previewMode: state.previewMode,
        onPreviewModeChange: (mode: UIActivityDisplayMode) => dispatchUi({ type: "PREVIEW_MODE", mode }),
        closePreview: () => dispatchUi({ type: "PREVIEW", preview: null }),
        failure: state.failure,
        dismissFailure: () => dispatchUi({ type: "FAILURE", message: null }),
        regenerateFlow: regenerateForm.flow,
        programSheet: regenerateForm.programSheet,
        gestures: {
            onOpenActivity: (student: UITutorPrescriptionStudent, activity: UITutorPrescriptionActivity) =>
                dispatchUi({
                    type: "PREVIEW",
                    preview: { kind: "existing", studentId: student.id, activityId: activity.id },
                }),
            onOpenProposal: (student: UITutorPrescriptionStudent, index: number) =>
                dispatchUi({ type: "PREVIEW", preview: { kind: "proposal", studentId: student.id, index } }),
            onAskRemoveActivity,
            onDropProposal,
            onRegenerate,
            onAddActivity,
            onConfirm,
            onUndo,
            onRetryDraft,
            onContinue,
            dialog: state.dialog?.shown ?? null,
            onDialogConfirm,
            onDialogClose: () => dispatchUi({ type: "DIALOG", dialog: null }),
        },
    }
}
